import React from "react"
import PropTypes from "prop-types"
import _ from "lodash"
import {
    Button,
    TextField,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress
} from "@mui/material"
import { DeleteOutline, UploadFileOutlined, AddLink } from "@mui/icons-material"
import {
    useAddLectureResourceMutation,
    useDeleteLectureResourceMutation
} from "../../Redux/actions/lecture.action"
import { useUploadImageMutation } from "../../Redux/actions/upload_content"
import ResourceList from "../components/ResourceList"
import ConfirmDialog from "../components/ConfirmDialog"

const isPdf = (file) =>
    file?.type === "application/pdf" || /\.pdf$/i.test(file?.name || "")

/**
 * Downloadable material attached to a saved lecture: a list with delete, a
 * form to add a link, and a PDF upload. PDFs go through the shared upload
 * API first; its URL then becomes a "file" resource. Each change saves
 * straight away -- it does not wait for the lecture form's Save button.
 */
export default function LectureResourcesEditor({ lecture }) {
    const fileInput = React.useRef(null)
    const [link, setLink] = React.useState({ title: "", url: "" })
    const [error, setError] = React.useState("")
    const [pendingDelete, setPendingDelete] = React.useState(null)
    const [uploading, setUploading] = React.useState(false)

    const [addAction, addState] = useAddLectureResourceMutation()
    const [deleteAction, deleteState] = useDeleteLectureResourceMutation()
    const [uploadAction] = useUploadImageMutation()

    const busy = uploading || addState.isLoading || deleteState.isLoading
    const resources = _.get(lecture, "resources", null) || []

    const failed = (failure, fallback) =>
        setError(_.get(failure, "data.detail", null) || fallback)

    const addLink = async () => {
        const title = link.title.trim()
        const url = link.url.trim()
        if (!title || !url) {
            setError("Give the link a title and a URL.")
            return
        }
        if (!/^https?:\/\//i.test(url)) {
            setError("Links must start with http:// or https://")
            return
        }
        setError("")
        try {
            await addAction({
                lecture_id: lecture.lecture_id,
                title,
                url,
                kind: "link"
            }).unwrap()
            setLink({ title: "", url: "" })
        } catch (failure) {
            failed(failure, "Could not add the link.")
        }
    }

    const uploadPdf = async (event) => {
        const file = event.target.files?.[0]
        /* Let the same file be picked again after a failure. */
        event.target.value = ""
        if (!file) return
        if (!isPdf(file)) {
            setError("Only PDF files can be uploaded.")
            return
        }
        setError("")
        setUploading(true)
        try {
            const uploaded = await uploadAction({ file }).unwrap()
            const url = _.get(uploaded, "url", null) || _.get(uploaded, "data.url", null)
            if (!url) throw new Error("missing url")
            await addAction({
                lecture_id: lecture.lecture_id,
                title: file.name.replace(/\.pdf$/i, "").slice(0, 300) || "Notes",
                url,
                kind: "file"
            }).unwrap()
        } catch (failure) {
            failed(failure, "Could not upload the PDF. Please try again.")
        } finally {
            setUploading(false)
        }
    }

    const confirmDelete = async () => {
        const resource = pendingDelete
        setPendingDelete(null)
        setError("")
        try {
            await deleteAction({ resource_id: resource.resource_id }).unwrap()
        } catch (failure) {
            failed(failure, "Could not remove the resource.")
        }
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-700">
                    Resources
                    <span className="ml-1 font-normal text-slate-400">
                        ({resources.length})
                    </span>
                </h3>
                {busy && <CircularProgress size={18} />}
            </div>

            {resources.length === 0 ? (
                <p className="text-xs text-slate-500">
                    Attach notes, worksheets or useful links. Students see them
                    under the video.
                </p>
            ) : (
                <ResourceList
                    resources={resources}
                    actions={(resource) => (
                        <Tooltip title="Remove resource">
                            <IconButton
                                size="small"
                                color="error"
                                disabled={busy}
                                onClick={() => setPendingDelete(resource)}
                            >
                                <DeleteOutline fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                />
            )}

            {/* Add a link */}
            <div className="flex flex-col gap-2 sm:flex-row">
                <TextField
                    size="small"
                    label="Link title"
                    value={link.title}
                    inputProps={{ maxLength: 300 }}
                    onChange={(event) =>
                        setLink((prev) => ({ ...prev, title: event.target.value }))
                    }
                    sx={{ flex: 1 }}
                />
                <TextField
                    size="small"
                    label="URL"
                    placeholder="https://"
                    value={link.url}
                    inputProps={{ maxLength: 1000 }}
                    onChange={(event) =>
                        setLink((prev) => ({ ...prev, url: event.target.value }))
                    }
                    sx={{ flex: 1.4 }}
                />
                <Button
                    variant="outlined"
                    startIcon={<AddLink />}
                    onClick={addLink}
                    disabled={busy}
                >
                    Add
                </Button>
            </div>

            {/* Upload a PDF */}
            <input
                ref={fileInput}
                type="file"
                accept="application/pdf,.pdf"
                hidden
                onChange={uploadPdf}
            />
            <Button
                size="small"
                startIcon={<UploadFileOutlined />}
                disabled={busy}
                onClick={() => fileInput.current?.click()}
            >
                {uploading ? "Uploading..." : "Upload a PDF"}
            </Button>

            {error && <Alert severity="error">{error}</Alert>}

            <ConfirmDialog
                open={Boolean(pendingDelete)}
                title="Remove this resource?"
                message={
                    pendingDelete
                        ? `"${pendingDelete.title}" will no longer be available to students.`
                        : ""
                }
                confirmLabel="Remove"
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </div>
    )
}

LectureResourcesEditor.propTypes = {
    lecture: PropTypes.object.isRequired
}
